import { queryOptions, useMutation, type UseMutationResult, type DefaultError } from '@tanstack/react-query';

import { FilterListHelpers } from '@utils/filterListHelpers';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';

import {
    OfficeCreateForm,
    OfficeData,
    OfficeListQuery,
    OfficeStatus,
    OfficeUpdateForm,
} from 'chaika-frontend-types/frontend-cms';
import type { Optional } from 'chaika-common-types';

import { OfficeApi } from '@organization/api/OfficeApi';
import { OfficeTypeApi } from '@organization/api/OfficeTypeApi';
import { queryClient } from '@store/queryClient';

export interface OfficeFilters {
    organizationId?: string[];
    status?: OfficeStatus;
    name?: string;
}

export const officeKeys = {
    all: ['offices'] as const,
    lists: () => [...officeKeys.all, 'list'] as const,
    list: (filters: object) => [...officeKeys.lists(), { filters }] as const,
    details: () => [...officeKeys.all, 'detail'] as const,
    detail: (id: string) => [...officeKeys.details(), id] as const,
    detailMany: (ids: string[]) => [...officeKeys.details(), { ids: getUniqSortedIds(ids) }] as const,
};

export const officeTypeKeys = {
    all: ['officeTypes'] as const,
    list: () => [...officeTypeKeys.all, 'list'] as const,
};

export const officeTypeQueryOptions = {
    list: () =>
        queryOptions({
            staleTime: Infinity,
            queryKey: officeTypeKeys.list(),
            queryFn: () => OfficeTypeApi.getList(),
        }),
};

export const officeQueryOptions = {
    list: (filters: OfficeFilters = {}) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: officeKeys.list(filters),
            queryFn: () => OfficeApi.getList(toApiQuery(filters)),
        }),
    byId: (id: string) =>
        queryOptions({
            staleTime: Infinity,
            queryKey: officeKeys.detail(id),
            queryFn: async () => {
                const offices = await OfficeApi.getList({ ids: id });
                return offices[0];
            },
            initialData: () => collectOfficesFromListCaches().get(id),
        }),
    byIds: (ids: string[]) => {
        const normalizedIds = getUniqSortedIds(ids);

        return queryOptions({
            staleTime: Infinity,
            queryKey: officeKeys.detailMany(normalizedIds),
            queryFn: () => OfficeApi.getList({ ids: normalizedIds.join(',') }),
            initialData: () => {
                const cached = collectOfficesFromListCaches();
                const found = normalizedIds.map(id => cached.get(id));
                return found.every(Boolean) ? (found as OfficeData[]) : undefined;
            },
        });
    },
};

// Loaders for non-React callers (event handlers, services, route loaders).
// Hit the network only if the R-Q cache doesn't already hold fresh data.
export function loadOfficeById(id: string): Promise<Optional<OfficeData>> {
    return queryClient.ensureQueryData(officeQueryOptions.byId(id));
}

export function loadOfficesByIds(ids: string[]): Promise<OfficeData[]> {
    return queryClient.ensureQueryData(officeQueryOptions.byIds(ids));
}

export function loadOffices(filters: OfficeFilters = {}): Promise<OfficeData[]> {
    return queryClient.ensureQueryData(officeQueryOptions.list(filters));
}

export function getOffices(filters: OfficeFilters = {}): OfficeData[] {
    const lists = queryClient.getQueriesData<OfficeData[]>({ queryKey: officeKeys.lists() });
    const seen = new Set<string>();
    const result: OfficeData[] = [];

    for (const [, offices] of lists) {
        if (!offices) continue;
        for (const office of offices) {
            if (seen.has(office.id)) continue;
            seen.add(office.id);
            if (matchesFilter(office, filters)) result.push(office);
        }
    }

    return result;
}

export function useCreateOffice(organizationId: string): UseMutationResult<void, DefaultError, OfficeCreateForm> {
    return useMutation({
        mutationFn: (params: OfficeCreateForm) => OfficeApi.create({ ...params, organizationId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: officeKeys.lists() });
        },
    });
}

export function useUpdateOffice(
    organizationId: string,
): UseMutationResult<void, DefaultError, { id: string; params: OfficeUpdateForm }> {
    return useMutation({
        mutationFn: ({ id, params }: { id: string; params: OfficeUpdateForm }) => OfficeApi.update(id, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: officeKeys.all });
        },
    });
}

export function useToggleOfficeStatus(
    organizationId: string,
): UseMutationResult<void, DefaultError, { id: string; currentStatus: OfficeStatus }> {
    return useMutation({
        mutationFn: ({ id, currentStatus }: { id: string; currentStatus: OfficeStatus }) =>
            OfficeApi.updateStatus(id, toggleStatus(currentStatus)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: officeKeys.all });
        },
    });
}

function toApiQuery({ organizationId, status }: OfficeFilters): OfficeListQuery {
    return {
        ...(organizationId?.length ? { organizationId: organizationId.join(',') } : {}),
        ...(status !== undefined && { status }),
    };
}

function matchesFilter(office: OfficeData, { organizationId, status, name }: OfficeFilters): boolean {
    const { filterFieldValueIncludesString } = FilterListHelpers;
    return (
        (!organizationId?.length || organizationId.includes(office.organizationId)) &&
        (!status || office.status === status) &&
        filterFieldValueIncludesString<OfficeData>(office, name, 'name')
    );
}

function toggleStatus(status: OfficeStatus): OfficeStatus {
    return status === OfficeStatus.Active ? OfficeStatus.Archived : OfficeStatus.Active;
}

function collectOfficesFromListCaches(): Map<string, OfficeData> {
    const lists = queryClient.getQueriesData<OfficeData[]>({ queryKey: officeKeys.lists() });
    const byId = new Map<string, OfficeData>();
    for (const [, offices] of lists) {
        if (!offices) continue;
        for (const office of offices) {
            if (!byId.has(office.id)) byId.set(office.id, office);
        }
    }
    return byId;
}
